import { isNil } from 'ramda';

import {
  validateEmail,
  validatePhoneNumber,
  validateRequired,
} from '../../../utils/validations';

import formMessages from '../../../containers/ShortLeaseApplication/messages';
import messages from './messages';

import type { ContactInformation } from './types';

const validateContactArray = (contacts, type, intl) => {
  const errors = [];

  if (!contacts || contacts.length === 0) {
    // Require at least one contact
    errors[0] =
      type === 'email'
        ? { emailAddress: intl.formatMessage(formMessages.required) }
        : { phoneNumber: intl.formatMessage(formMessages.required) };
    return errors;
  }

  contacts.forEach((contact, index) => {
    const contactErrors = {};

    if (type === 'email') {
      if (index === 0 && !validateRequired('emailAddress', contact)) {
        contactErrors.emailAddress = intl.formatMessage(formMessages.required);
      }
      if (contact.emailAddress && !validateEmail(contact.emailAddress)) {
        contactErrors.emailAddress = intl.formatMessage(
          formMessages.invalidValue,
        );
      }
    } else {
      if (index === 0 && !validateRequired('phoneNumber', contact)) {
        contactErrors.phoneNumber = intl.formatMessage(formMessages.required);
      }
      if (contact.phoneNumber && !validatePhoneNumber(contact.phoneNumber)) {
        contactErrors.phoneNumber = intl.formatMessage(
          formMessages.invalidValue,
        );
      }
    }

    if (Object.keys(contactErrors).length > 0) {
      errors[index] = contactErrors;
    }
  });

  // Check for duplicates
  const fieldName = type === 'email' ? 'emailAddress' : 'phoneNumber';
  const messageKey =
    type === 'email' ? 'duplicateEmail' : 'duplicatePhoneNumber';
  const valueMap = new Map();

  // First pass: map all values to their indices
  contacts.forEach((contact, index) => {
    const value = contact[fieldName];
    if (value && value.trim()) {
      const normalizedValue = value.trim().toLowerCase();
      if (!valueMap.has(normalizedValue)) {
        valueMap.set(normalizedValue, []);
      }
      valueMap.get(normalizedValue).push(index);
    }
  });

  // Second pass: mark duplicates
  valueMap.forEach((indices, value) => {
    if (indices.length > 1) {
      // Mark all instances as duplicates
      indices.forEach((index) => {
        if (!errors[index]) {
          errors[index] = {};
        }
        errors[index][fieldName] = intl.formatMessage(messages[messageKey]);
      });
    }
  });

  return errors;
};

const validate = (values: ContactInformation, { intl, flags }: Object) => {
  const errors = {};

  if (isNil(values)) {
    errors.emailAddress = intl.formatMessage(formMessages.required);
    return errors;
  }

  // Check feature flag to determine which fields to validate
  if (flags?.applicationAdditionalPhoneEmailAndTypes) {
    // Validate array format when feature flag is enabled
    if (values.emails) {
      const emailErrors = validateContactArray(values.emails, 'email', intl);
      if (emailErrors.length > 0) {
        errors.emails = emailErrors;
      }
    }

    if (values.phones) {
      const phoneErrors = validateContactArray(values.phones, 'phone', intl);
      if (phoneErrors.length > 0) {
        errors.phones = phoneErrors;
      }
    }
  } else {
    // Legacy validation when feature flag is disabled
    if (!validateRequired('emailAddress', values)) {
      errors.emailAddress = intl.formatMessage(formMessages.required);
    }
    if (!validateRequired('phoneNumber', values)) {
      errors.phoneNumber = intl.formatMessage(formMessages.required);
    }

    if (!errors.emailAddress && !validateEmail(values.emailAddress)) {
      errors.emailAddress = intl.formatMessage(formMessages.invalidValue);
    }

    if (!errors.phoneNumber && !validatePhoneNumber(values.phoneNumber)) {
      errors.phoneNumber = intl.formatMessage(formMessages.invalidValue);
    }
  }

  return errors;
};

export default validate;
